/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { AutoComplete, Input } from "@duxton-web/ui"
import { Form } from "grab-ui"
import { SearchUniversal, ArrowRightUniversal } from "@duxton-web/icons"
import { storageHelper } from "utils/storage"
import { useTranslation } from "common/utils/i18n"
import { compact, find, isEmpty } from "lodash"
import { FC } from "react"
import Highlighter from "react-highlight-words"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { ModifierGroup } from "types/models/menu"
import { SearchNotFound } from "./SearchNotFound"
import {
  SEARCH_FIELD,
  FILTER_LIMIT,
  RECENT_LIMIT,
  SearchCategory,
} from "./searchBarConstants"

interface SearchBarProps extends FormComponentProps {
  placeholder: string
  onSearch?(value: string): void
  onSelect?(value: string): void
}

export const ModifierGroupSearchComponent: FC<SearchBarProps> = ({
  placeholder,
  onSearch,
  onSelect,
  form,
}: SearchBarProps) => {
  const { getFieldDecorator, getFieldValue, resetFields, setFieldsValue } = form

  const {
    merchantID,
    menu: { modifierGroups },
    menuGroupDetail: { menuGroupID },
    merchantGroupID,
  } = useSelector((state: AppState) => state.menu)

  const labels: any = merchantID
    ? { STORE_ID: merchantID }
    : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }

  const recentModifierGroupIDs = storageHelper.getModifierGroupSearchHistory(
    merchantID,
  )

  const renderOption = (
    record: ModifierGroup,
    rank: number,
    searchCategory: string,
  ) => {
    return {
      key: `${searchCategory}_${record.modifierGroupID}`,
      value: `${searchCategory}_${record.modifierGroupID}`,
      record,
      rank,
      searchCategory,
      label: (
        <div
          css={css`
            font-size: 14px;
            &:not(:hover) {
              .edit_hover_class {
                visibility: hidden;
              }
            }
          `}
        >
          <Highlighter
            highlightTag="span"
            highlightStyle={{
              fontWeight: 500,
            }}
            autoEscape
            searchWords={[getFieldValue(SEARCH_FIELD) ?? ""]}
            textToHighlight={record?.modifierGroupName ?? ""}
          />
          <span className="edit_hover_class">
            <ArrowRightUniversal style={{ color: "#0091FF" }} />
          </span>
        </div>
      ),
    }
  }

  const filteredSource = getFieldValue(SEARCH_FIELD)
    ? modifierGroups
        .filter((val: ModifierGroup) =>
          val?.modifierGroupName
            ?.toLowerCase()
            .includes(getFieldValue(SEARCH_FIELD)?.toLowerCase()),
        )
        .slice(0, FILTER_LIMIT)
    : []

  // @ts-ignore
  const recentModifierGroups: ModifierGroup[] = recentModifierGroupIDs
    .map(ID =>
      find(
        modifierGroups,
        modifierGroup => modifierGroup.modifierGroupID === ID,
      ),
    )
    .filter(mg => !isEmpty(mg))
    .slice(0, RECENT_LIMIT)

  const recentlySearchedTrans = useTranslation("menu.overview.recentlySearched")

  const dataSource = compact([
    // Not found placeholder
    !filteredSource?.length &&
      getFieldValue(SEARCH_FIELD) && {
        key: "not found",
        value: "not found",
        disabled: true,
        label: <SearchNotFound search={getFieldValue(SEARCH_FIELD)} />,
      },
    // Filtered data
    ...filteredSource.map((val: any, idx: number) =>
      renderOption(val, idx + 1, SearchCategory.results),
    ),
    // Recent data
    recentModifierGroups?.length && {
      key: SearchCategory.recent,
      label: (
        <span
          css={css`
            font-weight: 500;
            font-size: 12px;
            line-height: 14px;
            letter-spacing: 0.5px;
            text-transform: uppercase;
          `}
        >
          {recentlySearchedTrans}
        </span>
      ),
      options: recentModifierGroups.map((rec: any, idx: number) =>
        renderOption(
          rec,
          filteredSource.length + 1 + idx,
          SearchCategory.recent,
        ),
      ),
    },
  ])

  const handleClearInput = () => {
    resetFields()
    onSearch?.("")
  }

  const handleSelect = (
    _: string,
    option: {
      props: {
        record: ModifierGroup
        rank: number
        searchCategory?: SearchCategory
      }
    },
  ) => {
    const { rank, record, searchCategory } = option.props
    // small hack
    setTimeout(() => {
      setFieldsValue({
        [SEARCH_FIELD]: record?.modifierGroupName,
      })
    }, 0)

    storageHelper.setModifierGroupSearchHistory({
      merchantID,
      modifierGroupIDs: compact([
        record.modifierGroupID,
        ...recentModifierGroupIDs,
      ]),
    })

    TroyWebTracker.sendEvent({
      category: EventCategory.menuModifierGroupsTab,
      action: EventAction.selectModifiergroupSearchresult,
      labels: {
        ...labels,
        SEARCH_TEXT: record?.modifierGroupName,
        SEARCH_RESULT_RANK: rank,
        SEARCH_CATEGORY: searchCategory,
        MODIFIER_GROUP_ID: record?.modifierGroupID,
      },
    })

    onSelect?.(record.modifierGroupName)
  }

  const handleAutocompleteClick = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuModifierGroupsTab,
      action: EventAction.clickModifiergroupSearch,
      labels,
    })
  }

  return (
    <div>
      {getFieldDecorator(SEARCH_FIELD, {
        initialValue: "",
      })(
        <AutoComplete
          css={css`
            width: 360px;
            float: right;
          `}
          options={dataSource}
          onSelect={handleSelect}
          onSearch={onSearch}
          onClear={handleClearInput}
          disabled={!modifierGroups?.length}
          allowClear
        >
          <Input
            data-testid="search-input"
            placeholder={placeholder}
            prefix={<SearchUniversal />}
            onClick={handleAutocompleteClick}
            autoComplete="off"
          />
        </AutoComplete>,
      )}
    </div>
  )
}
export const ModifierGroupSearch = Form.create<SearchBarProps>({})(
  ModifierGroupSearchComponent,
)
